import { ApiResponse, Client, ClientOverview, ClientVoiceQuote, DashboardData, Provider, SubmitQuestionRequest, UpdateClientOverviewRequest, User, Contact } from '../types';
import { apiBaseUrl } from '../config/environment';

const API_BASE_URL = apiBaseUrl;

class ApiService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options?.headers,
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          // Handle unauthorized - just throw error, let Auth0 handle the flow
          throw new Error('Authentication required');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result: ApiResponse<T> = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'API request failed');
      }
      
      return result.data;
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Set authentication token
  setAuthToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  // Clear authentication token
  clearAuthToken(): void {
    localStorage.removeItem('auth_token');
  }

  // User endpoints
  async getCurrentUser(): Promise<User> {
    return this.request<User>('/users/me');
  }

  async getAllUsers(): Promise<User[]> {
    return this.request<User[]>('/users');
  }

  async updateUserRole(userId: string, role: string): Promise<User> {
    return this.request<User>(`/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  async updateCurrentUserRole(role: string): Promise<User> {
    return this.request<User>(`/users/me/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  // Dashboard endpoints
  async getDashboard(clientId: string): Promise<DashboardData> {
    return this.request<DashboardData>(`/dashboard?clientId=${encodeURIComponent(clientId)}`);
  }

  async updateAISummary(clientId: string): Promise<{ message: string; summary?: string }> {
    return this.request<{ message: string; summary?: string }>(`/dashboard/ai-summary/${clientId}`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Client CRUD endpoints
  async getClients(): Promise<{ data: Client[] }> {
    return this.request<{ data: Client[] }>('/clients');
  }

  async createClient(data: { name: string; country: string }): Promise<{ data: Client }> {
    return this.request<{ data: Client }>('/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteClient(clientId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/clients/${clientId}`, {
      method: 'DELETE',
    });
  }

  async updateClient(clientId: string, data: { name: string; country: string; shortDescription?: string }): Promise<Client> {
    return this.request<Client>(`/clients/${clientId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async moveClientUp(clientId: string): Promise<Client> {
    return this.request<Client>(`/clients/${clientId}/move-up`, {
      method: 'PATCH',
    });
  }

  async moveClientDown(clientId: string): Promise<Client> {
    return this.request<Client>(`/clients/${clientId}/move-down`, {
      method: 'PATCH',
    });
  }

  async getClientOverview(clientId: string): Promise<ClientOverview> {
    return this.request<ClientOverview>(`/clients/${clientId}`);
  }

  async updateClientOverview(clientId: string, updateData: UpdateClientOverviewRequest): Promise<ClientOverview> {
    return this.request<ClientOverview>(`/clients/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  async updateInsights(clientId: string, insights: any[]): Promise<any> {
    return this.request<any>(`/dashboard/insights/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify({ insights }),
    });
  }

  async updateTimeline(clientId: string, timeline: any[]): Promise<any> {
    return this.request<any>(`/dashboard/timeline/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify({ timeline }),
    });
  }

  async updateProjectPhases(clientId: string, phases: any[], overallStatus: string): Promise<any> {
    return this.request<any>(`/dashboard/project-phases/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify({ phases, overallStatus }),
    });
  }

  async getProviders(): Promise<Provider[]> {
    return this.request<Provider[]>('/clients/providers/all');
  }

  // Client Voice endpoints
  async getClientVoice(clientId: string): Promise<ClientVoiceQuote[]> {
    return this.request<ClientVoiceQuote[]>(`/clients/${clientId}/voice`);
  }

  async createClientVoice(clientId: string, voiceData: { quote: string; source: string }): Promise<ClientVoiceQuote> {
    return this.request<ClientVoiceQuote>(`/clients/${clientId}/voice`, {
      method: 'POST',
      body: JSON.stringify(voiceData),
    });
  }

  async updateClientVoice(clientId: string, voiceId: string, voiceData: { quote: string; source: string }): Promise<ClientVoiceQuote> {
    return this.request<ClientVoiceQuote>(`/clients/${clientId}/voice/${voiceId}`, {
      method: 'PUT',
      body: JSON.stringify(voiceData),
    });
  }

  async deleteClientVoice(clientId: string, voiceId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/clients/${clientId}/voice/${voiceId}`, {
      method: 'DELETE',
    });
  }

  // Questions endpoints
  async submitQuestion(questionData: SubmitQuestionRequest): Promise<{ message: string }> {
    return this.request<{ message: string }>('/questions/submit', {
      method: 'POST',
      body: JSON.stringify(questionData),
    });
  }

  // Contact endpoints
  async getContactsByProvider(providerId: string): Promise<Contact[]> {
    return this.request<Contact[]>(`/contacts/provider/${providerId}`);
  }

  async getContactById(contactId: string): Promise<Contact> {
    return this.request<Contact>(`/contacts/${contactId}`);
  }

  async createContact(contactData: { name: string; role: string; email?: string; phone?: string; providerId: string }): Promise<Contact> {
    return this.request<Contact>('/contacts', {
      method: 'POST',
      body: JSON.stringify(contactData),
    });
  }

  async updateContact(contactId: string, contactData: { name?: string; role?: string; email?: string; phone?: string; providerId?: string }): Promise<Contact> {
    return this.request<Contact>(`/contacts/${contactId}`, {
      method: 'PUT',
      body: JSON.stringify(contactData),
    });
  }

  async deleteContact(contactId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/contacts/${contactId}`, {
      method: 'DELETE',
    });
  }
}

export const apiService = new ApiService(); 